import React, {useEffect, useRef, useState} from 'react';
import {Prompt} from 'react-router-dom';
import styled from 'styled-components';
import {NotificationManager} from 'react-notifications';

import Button from './Button';
import ProgressBar from './Progressbar';
import PrinterPickerDialog, {PrinterDialogFrame, DialogButtons} from './PrinterPickerDialog';
import * as api from '../lib/api';
import {getPrinters, printZpl, rememberPrinterUid} from '../lib/zebraPrint';
import {preferredPrinterUid, deviceLabel, describeError} from '../lib/zebraPrintUi';

// Rows per batch. Lower it if large layouts (^GF graphics) fail on real hardware; the API caps it at 100.
export const CHUNK_SIZE = 50;

export const MAX_JOB_ROWS = 1000;

const LARGE_JOB_THRESHOLD = 100;

/**
 * Prints labels for many rows via Zebra Browser Print, rendering and sending them in batches of CHUNK_SIZE.
 * Failures are never retried automatically, since a retry could double-print a partly received batch.
 */
function BulkPrintLabelsModal({templateId, rowIds, credentials, open, onClose}) {
  const [phase, setPhase] = useState('loading'); // 'loading' | 'confirm' | 'printing' | 'done'
  const [devices, setDevices] = useState([]);
  const [selectedUid, setSelectedUid] = useState(null);
  const [progress, setProgress] = useState({processed: 0, total: 0});
  const [stopping, setStopping] = useState(false);
  const [result, setResult] = useState(null);
  const [wasOpen, setWasOpen] = useState(false);

  // Bumped on close/unmount so in-flight async work stops.
  const sessionRef = useRef(0);
  const cancelRef = useRef(false);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setPhase('loading');
      setDevices([]);
      setSelectedUid(null);
      setResult(null);
      setStopping(false);
    }
  }

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const session = sessionRef.current;

    (async () => {
      try {
        const {devices: found, defaultUid} = await getPrinters();
        if (session !== sessionRef.current) {
          return;
        }

        if (found.length === 0) {
          NotificationManager.error(
            'No printers are set up in Browser Print on this computer. Add your printer in the Browser '
            + 'Print app, then try again.', 'No printers found');
          onClose();
          return;
        }

        setDevices(found);
        setSelectedUid(preferredPrinterUid(found, defaultUid));
        setPhase('confirm');
      }
      catch (err) {
        if (session === sessionRef.current) {
          NotificationManager.error(describeError(err, 'bulk'), 'Could not print');
          onClose();
        }
      }
    })();

    return () => {
      sessionRef.current += 1;
    };
  }, [open]); // Only re-run when the modal opens or closes.

  // Warn before a reload or tab close would abandon a running job.
  useEffect(() => {
    if (phase !== 'printing') {
      return undefined;
    }

    const handleBeforeUnload = e => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [phase]);

  if (!open) {
    return null;
  }

  const runJob = async (device, ids, carried = {labelsSent: 0, failures: []}) => {
    const session = sessionRef.current;
    const isActive = () => session === sessionRef.current;

    cancelRef.current = false;
    setStopping(false);
    setResult(null);
    setProgress({processed: 0, total: ids.length});
    setPhase('printing');

    let labelsSent = carried.labelsSent;
    let failures = carried.failures;
    let remembered = false;
    let stoppedAt = ids.length;
    let stop = null; // {reason: 'cancelled'} or {reason: 'error', message, mayHavePartlyPrinted}

    for (let start = 0; start < ids.length; start += CHUNK_SIZE) {
      if (cancelRef.current) {
        stoppedAt = start;
        stop = {reason: 'cancelled'};
        break;
      }

      const chunk = ids.slice(start, start + CHUNK_SIZE);
      // Only a failure while sending can leave a partly printed batch.
      let sending = false;

      try {
        const response = await api.renderRowLabels(templateId, chunk, credentials, deviceLabel(device));
        if (!isActive()) {
          return; // Closed or unmounted while rendering: send nothing more.
        }

        const labels = response.labels || [];
        if (labels.length > 0) {
          sending = true;
          await printZpl(device, labels.map(label => label.zpl).join('\n'));
          if (!remembered) {
            rememberPrinterUid(device.uid);
            remembered = true;
          }
          labelsSent += labels.length;
        }

        failures = failures.concat(response.failures || []);
      }
      catch (err) {
        if (!isActive()) {
          return;
        }
        stoppedAt = start;
        stop = {reason: 'error', message: describeError(err, 'bulk'), mayHavePartlyPrinted: sending};
        break;
      }

      if (!isActive()) {
        return;
      }
      setProgress({processed: Math.min(start + CHUNK_SIZE, ids.length), total: ids.length});
    }

    setResult({device, labelsSent, failures, unsentRowIds: ids.slice(stoppedAt), stop});
    setPhase('done');
  };

  const handlePrint = () => {
    if (rowIds.length === 0 || rowIds.length > MAX_JOB_ROWS) {
      return;
    }
    const device = devices.find(d => d.uid === selectedUid) || devices[0];
    runJob(device, rowIds);
  };

  const handlePrintRemaining = () => {
    const {device, unsentRowIds, labelsSent, failures} = result;
    runJob(device, unsentRowIds, {labelsSent, failures});
  };

  const handleCancel = () => {
    cancelRef.current = true;
    setStopping(true);
  };

  const count = rowIds.length;
  const leavePrompt = (
    <Prompt when={phase === 'printing'} message="Labels are still printing. Leave this page and stop the job?" />
  );

  if (phase === 'loading') {
    return (
      <PrinterDialogFrame title={`Print ${plural(count, 'label')}`} onDismiss={onClose}>
        <Message>Looking for printers...</Message>
        <DialogButtons>
          <Button secondary type="button" onClick={onClose}>Cancel</Button>
        </DialogButtons>
      </PrinterDialogFrame>
    );
  }

  if (phase === 'confirm') {
    const device = devices.find(d => d.uid === selectedUid) || devices[0];
    const tooMany = count > MAX_JOB_ROWS;

    return (
      <PrinterPickerDialog
        title={`Print ${plural(count, 'label')}`}
        devices={devices}
        selectedUid={selectedUid}
        onChange={setSelectedUid}
        onConfirm={handlePrint}
        onCancel={onClose}
        confirmDisabled={tooMany || count === 0}>
        {count === 0 && <Message>There are no rows to print.</Message>}
        {tooMany && (
          <Message tone="error">
            You can print up to {MAX_JOB_ROWS.toLocaleString()} labels at a time. Narrow your filters
            to {MAX_JOB_ROWS.toLocaleString()} rows or fewer, then try again.
          </Message>
        )}
        {!tooMany && count > LARGE_JOB_THRESHOLD && (
          <Message tone="warning">
            This will print {plural(count, 'label')} on {deviceLabel(device)}.
          </Message>
        )}
      </PrinterPickerDialog>
    );
  }

  if (phase === 'printing') {
    const percentage = progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0;

    return (
      <>
        {leavePrompt}
        <PrinterDialogFrame title="Printing labels">
          <Message>
            Sent {progress.processed.toLocaleString()} of {progress.total.toLocaleString()}
          </Message>
          <ProgressWrapper>
            <ProgressBar percentage={percentage} />
          </ProgressWrapper>
          <Message muted>
            {stopping
              ? 'Stopping after the current batch...'
              : 'Keep this page open until printing finishes.'}
          </Message>
          <DialogButtons>
            <Button secondary type="button" disabled={stopping} onClick={handleCancel}>Cancel</Button>
          </DialogButtons>
        </PrinterDialogFrame>
      </>
    );
  }

  // Done.
  const {device, labelsSent, failures, unsentRowIds, stop} = result;
  const skipped = groupFailuresByReason(failures);
  const title = !stop ? 'Labels sent' : stop.reason === 'cancelled' ? 'Printing cancelled' : 'Printing stopped';

  return (
    <PrinterDialogFrame title={title} onDismiss={onClose}>
      <Message>
        {plural(labelsSent, 'label')} sent to {deviceLabel(device)}.
      </Message>

      {skipped.length > 0 && (
        <>
          <Message>{plural(failures.length, 'row')} skipped:</Message>
          <ReasonList>
            {skipped.map(({reason, count: reasonCount}) => (
              <li key={reason}>{plural(reasonCount, 'row')}: {reason}</li>
            ))}
          </ReasonList>
        </>
      )}

      {unsentRowIds.length > 0 && (
        stop.reason === 'cancelled' ? (
          <Message>{plural(unsentRowIds.length, 'row')} not sent because printing was cancelled.</Message>
        ) : (
          <>
            <Message tone="error">
              {plural(unsentRowIds.length, 'row')} not sent because printing stopped. {stop.message}
            </Message>
            {stop.mayHavePartlyPrinted && (
              <Message tone="warning">
                The batch that was printing when it stopped may have partly printed. Check the printer before
                printing the remaining rows so you don't get duplicates.
              </Message>
            )}
          </>
        )
      )}

      <DialogButtons>
        {unsentRowIds.length > 0 && (
          <Button type="button" onClick={handlePrintRemaining}>
            Print remaining ({unsentRowIds.length.toLocaleString()})
          </Button>
        )}
        <Button secondary={unsentRowIds.length > 0} type="button" onClick={onClose}>
          {unsentRowIds.length > 0 ? 'Close' : 'Done'}
        </Button>
      </DialogButtons>
    </PrinterDialogFrame>
  );
}

function plural(count, word) {
  return `${count.toLocaleString()} ${word}${count === 1 ? '' : 's'}`;
}

// Groups API failures into [{reason, count}], most common reason first.
function groupFailuresByReason(failures) {
  const counts = failures.reduce((map, failure) => {
    const reason = failure.reason || 'Unknown reason';
    map[reason] = (map[reason] || 0) + 1;
    return map;
  }, {});

  return Object.keys(counts)
    .map(reason => ({reason, count: counts[reason]}))
    .sort((a, b) => b.count - a.count);
}

const Message = styled.p`
  margin: 1rem 0 0 0;
  line-height: 1.4;
  padding: ${props => props.tone ? '0.75rem' : '0'};
  border-radius: 0.25rem;
  background: ${props => props.tone === 'error' ? '#fdecea' : props.tone === 'warning' ? '#fff4e5' : 'transparent'};
  color: ${props => props.tone === 'error' ? '#9b1c1c' : props.tone === 'warning' ? '#7a4a00' : props.muted ? '#6b6b76' : 'inherit'};

  h4 + & {
    margin-top: 0;
  }
`;

const ProgressWrapper = styled.div`
  margin-top: 0.75rem;
`;

const ReasonList = styled.ul`
  margin: 0.5rem 0 0 0;
  padding-left: 1.25rem;
  max-height: 10rem;
  overflow-y: auto;
`;

export default BulkPrintLabelsModal;
